//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

//  emit WithdrawRequested(owner, assets, shares);
import * as yup from 'yup'
import { InferType } from 'yup'
import { StringTypeEnum, toFieldMetaData } from './SchemaTypes'
import { PoolBaseAccountCTOSchemaValues } from './PoolBaseAccountCTO'
import { PoolFlexAccountOnlyCTOSchemaValues } from './PoolFlexAccountCTO'
import { PoolAccountOnlyCTOSchemaValues } from './PoolTermAccountCTO'

export enum ReportDataShape {
  UNKNOWN = 'UNKNOWN',
  POOL_EVENTS = 'POOL_EVENTS',
  POOL_EVENTS_LENDER = 'POOL_EVENTS_LENDER',
  LENDER_TIME_PERIOD_TERM = 'LENDER_TIME_PERIOD_TERM',
  LENDER_TIME_PERIOD_FLEX = 'LENDER_TIME_PERIOD_FLEX',
  LENDER_TIME_PERIOD_DYNAMIC = 'LENDER_TIME_PERIOD_DYNAMIC',
  LENDER_TIME_PERIOD_TERM_LENDER = 'LENDER_TIME_PERIOD_TERM_LENDER',
  LENDER_TIME_PERIOD_FLEX_LENDER = 'LENDER_TIME_PERIOD_FLEX_LENDER',
  LENDER_TIME_PERIOD_DYNAMIC_LENDER = 'LENDER_TIME_PERIOD_DYNAMIC_LENDER',
  POOL_OVERVIEW_FLEX = 'POOL_OVERVIEW_FLEX',
  POOL_OVERVIEW_TERM = 'POOL_OVERVIEW_TERM',
  POOL_OVERVIEW_DYNAMIC = 'POOL_OVERVIEW_DYNAMIC',
  PERFORMANCE = 'PERFORMANCE',
}
export const ReportDataShapeLabel = {
  UNKNOWN: 'Select report..',
  POOL_EVENTS: 'Transaction Ledger',
  POOL_EVENTS_LENDER: 'Transaction Ledger',
  LENDER_TIME_PERIOD_TERM: 'Fixed Term - Daily & Summary',
  LENDER_TIME_PERIOD_FLEX: 'Flexible Term - Daily & Summary',
  LENDER_TIME_PERIOD_DYNAMIC: 'Dynamic - Daily & Summary',
  LENDER_TIME_PERIOD_TERM_LENDER: 'Fixed Term - Daily & Summary',
  LENDER_TIME_PERIOD_FLEX_LENDER: 'Flexible Term - Daily & Summary',
  LENDER_TIME_PERIOD_DYNAMIC_LENDER: 'Dynamic - Daily & Summary',
  POOL_OVERVIEW_FLEX: 'Flexible Term - Overview',
  POOL_OVERVIEW_TERM: 'Fixed Term - Overview',
  POOL_OVERVIEW_DYNAMIC: 'Dynamic - Overview',
  PERFORMANCE: 'Performance',
}

export const ReportDataShapesForAdmin = [
  ReportDataShape.UNKNOWN,
  ReportDataShape.POOL_EVENTS,
  ReportDataShape.LENDER_TIME_PERIOD_TERM,
  ReportDataShape.LENDER_TIME_PERIOD_FLEX,
  ReportDataShape.LENDER_TIME_PERIOD_DYNAMIC,
  ReportDataShape.POOL_OVERVIEW_FLEX,
  ReportDataShape.POOL_OVERVIEW_TERM,
  ReportDataShape.POOL_OVERVIEW_DYNAMIC,
  ReportDataShape.PERFORMANCE,
]
export const ReportDataShapesForLender = [
  ReportDataShape.UNKNOWN,
  ReportDataShape.LENDER_TIME_PERIOD_TERM_LENDER,
  ReportDataShape.LENDER_TIME_PERIOD_FLEX_LENDER,
  ReportDataShape.LENDER_TIME_PERIOD_DYNAMIC_LENDER,
  ReportDataShape.POOL_EVENTS_LENDER,
]

export const ReportDefinitionSchema = yup.object({
  reportUuid: yup
    .string()
    .uuid()
    .required()
    .label('Report Id')
    .meta(toFieldMetaData({ propName: 'reportUuid' })),
  timestamp: yup
    .number()
    .required()
    .label('Timestamp')
    .meta(toFieldMetaData({ propName: 'timestamp', stringType: StringTypeEnum.UNIX_DATE })),

  companyUuid: yup
    .string()
    .uuid()
    .required()
    .label('Company Id')
    .meta(toFieldMetaData({ propName: 'companyUuid' })),

  reportName: yup
    .string()
    .required()
    .label('reportName')
    .meta(toFieldMetaData({ propName: 'reportName' })),
  isGlobal: yup
    .boolean()
    .label('isGlobal')
    .meta(toFieldMetaData({ propName: 'isGlobal' })),
  defaultForShape: yup
    .boolean()
    .label('defaultForShape')
    .meta(toFieldMetaData({ propName: 'defaultForShape' })),

  dataShape: yup
    .string()
    .label('dataShape')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'dataShape',
        stringType: StringTypeEnum.STRING_ENUM,
        enumValues: Object.values(ReportDataShape),
      })
    ),

  xGridDefinition: yup
    .object({})
    .optional()
    .label('xGridDefinition')
    .meta(
      toFieldMetaData({
        propName: 'xGridDefinition',
      })
    ),
})

export interface ReportDefinitionCTO extends InferType<typeof ReportDefinitionSchema> {
  xGridDefinition?: object | undefined
}

export function PoolReportingAccountCTOSchema(properties?: string[]): yup.ObjectSchema<any> {
  if (!properties) {
    return yup.object({
      ...PoolBaseAccountCTOSchemaValues,
      ...PoolFlexAccountOnlyCTOSchemaValues,
      ...PoolAccountOnlyCTOSchemaValues,
    })
  } else {
    const schemaValues: Record<string, yup.Schema> = {}
    properties.forEach((prop) => {
      if (PoolBaseAccountCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolBaseAccountCTOSchemaValues[prop]
      }
      if (PoolFlexAccountOnlyCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolFlexAccountOnlyCTOSchemaValues[prop]
      }
      if (PoolAccountOnlyCTOSchemaValues[prop]) {
        schemaValues[prop] = PoolAccountOnlyCTOSchemaValues[prop]
      }
      if (!schemaValues[prop]) {
        throw new Error(`Property ${prop} not found in PoolReportingAccountCTOSchema`)
      }
    })

    return yup.object(schemaValues)
  }
}
